import {
  computed,
  ref,
  toValue,
  type MaybeRefOrGetter,
  type Ref,
  type WritableComputedRef,
} from "vue";
import {
  getDateRange,
  DATE_RANGE_PRESET_VALUES,
} from "@lemmonhq/blogic/date-time";
import type { DateRangeType } from "@lemmonhq/types";
import { DAY_END, DAY_START, type DateRange, type TimeOfDay } from "./types";

/** `HH:mm` window applied to a stored range that carries no time of its own. */
export type SyncedTimeWindow = { startTime: TimeOfDay; endTime: TimeOfDay };

const STORAGE_PREFIX = "lemmon.dateRange.";

type Store = {
  state: Ref<DateRange>;
  /** Last value this tab wrote, so a `storage` echo is not written back. */
  lastWritten: string;
};

/**
 * One store per key, shared by every `syncedRef` call in the page. Two
 * components asking for the same key get the same reactive value, not two
 * copies that happen to agree at load time.
 */
const stores = new Map<string, Store>();

const storageKey = (key: string) => `${STORAGE_PREFIX}${key}`;

const isDateOnly = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const isTimeOfDay = (value: unknown): value is TimeOfDay =>
  typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

const isKnownType = (value: unknown): value is DateRangeType =>
  value === "custom" ||
  (DATE_RANGE_PRESET_VALUES as readonly unknown[]).includes(value);

/**
 * Parse a persisted range, rejecting anything malformed so a hand-edited or
 * outdated entry degrades to the caller's default instead of breaking the page.
 *
 * Presets are recomputed rather than trusted: "today" written yesterday has to
 * mean today when it is read back, otherwise every report would silently open
 * on a stale day.
 */
export const parseStoredRange = (raw: string | null): DateRange | null => {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const { type, startDate, endDate, startTime, endTime } = parsed as Record<
    string,
    unknown
  >;
  if (!isKnownType(type)) return null;

  let bounds: { startDate: string; endDate: string };
  if (type === "custom") {
    if (!isDateOnly(startDate) || !isDateOnly(endDate)) return null;
    if (startDate > endDate) return null;
    bounds = { startDate, endDate };
  } else {
    bounds = getDateRange(type);
  }

  const range: DateRange = { type, ...bounds };
  if (isTimeOfDay(startTime)) range.startTime = startTime;
  if (isTimeOfDay(endTime)) range.endTime = endTime;
  return range;
};

const readStored = (key: string): DateRange | null => {
  try {
    return parseStoredRange(localStorage.getItem(storageKey(key)));
  } catch {
    // Private mode / storage disabled: fall back to the in-memory value.
    return null;
  }
};

const persist = (key: string, store: Store) => {
  const serialized = JSON.stringify(store.state.value);
  if (serialized === store.lastWritten) return;
  store.lastWritten = serialized;
  try {
    localStorage.setItem(storageKey(key), serialized);
  } catch {
    // Nothing to do — the value still lives in the shared store for this tab.
  }
};

const getStore = (key: string, fallback: DateRange): Store => {
  const existing = stores.get(key);
  if (existing) return existing;

  const stored = readStored(key);
  const store: Store = {
    state: ref(stored ?? fallback),
    lastWritten: stored ? JSON.stringify(stored) : "",
  };
  stores.set(key, store);

  // A second tab (or the same app opened twice in the WebView) picking a range
  // should move this one too, which is the whole point of sharing the key.
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== storageKey(key)) return;
      const next = parseStoredRange(event.newValue);
      if (!next) return;
      store.lastWritten = JSON.stringify(next);
      store.state.value = next;
    });
  }

  return store;
};

/**
 * A `DateRange` ref shared across every page that asks for the same `key` and
 * persisted to `localStorage`, so the report pages open on the range the user
 * last picked instead of each resetting to their own default.
 *
 * `defaultValue` only applies the first time a key is used in a session and
 * nothing is stored — the pages sharing a key are expected to be interchangeable
 * views of one selection, not holders of competing defaults.
 *
 * `defaultTime` is for callers that render the picker with `showTime`: the
 * stored range may come from a date-only page and carry no time at all, so this
 * window (for the Data Room, the restaurant's configured opening hours) fills
 * it in on read. It accepts a getter/ref because those settings arrive async.
 */
export function syncedRef(
  key: string,
  defaultValue: DateRange | (() => DateRange),
  defaultTime?: MaybeRefOrGetter<SyncedTimeWindow | null | undefined>,
): WritableComputedRef<DateRange> {
  const store = getStore(
    key,
    typeof defaultValue === "function" ? defaultValue() : defaultValue,
  );

  return computed<DateRange>({
    get() {
      const value = store.state.value;
      const window = toValue(defaultTime);
      if (!window) return value;
      return {
        ...value,
        startTime: value.startTime ?? window.startTime ?? DAY_START,
        endTime: value.endTime ?? window.endTime ?? DAY_END,
      };
    },
    set(next) {
      const value: DateRange = {
        type: next.type,
        startDate: next.startDate,
        endDate: next.endDate,
      };
      const window = toValue(defaultTime);

      if (!window) {
        // Date-only pages emit no time fields at all. Carrying the stored ones
        // forward keeps a Data Room time window alive across a range change
        // made on a page that has no time controls to preserve it with.
        const startTime = next.startTime ?? store.state.value.startTime;
        const endTime = next.endTime ?? store.state.value.endTime;
        if (startTime) value.startTime = startTime;
        if (endTime) value.endTime = endTime;
      } else if (
        next.startTime !== window.startTime ||
        next.endTime !== window.endTime
      ) {
        // A time-aware caller always sends both fields, so anything matching
        // its own default is not a choice the user made — storing it would
        // freeze today's opening hours and stop `defaultTime` from tracking a
        // later change in restaurant settings. Only a real override persists.
        if (next.startTime) value.startTime = next.startTime;
        if (next.endTime) value.endTime = next.endTime;
      }

      store.state.value = value;
      persist(key, store);
    },
  });
}

/** Test seam: drop the in-memory stores so a fresh read hits `localStorage`. */
export const resetSyncedRefs = () => stores.clear();
